import type {
  Channel,
  Coverage,
  OfferViewModel,
  ProductType,
  Quote,
} from "@/api_client";

import { getProductName } from "../../utils/productName";
import type {
  EcommerceAdd,
  EcommerceCheckout,
  EcommerceCheckoutOption,
  EcommerceEntry,
  EcommerceProduct,
  EcommercePurchase,
  EcommerceRemove,
  EcommerceVerifyAndBuy,
} from "../types/ecommerse";
import { getAgpId } from "./agpId";
import { getCoverage } from "./getCoverage";
import { getOfferPrice } from "./price";

export const getEcommerceAdd = (
  product: Quote,
  channel: Channel,
  coverageContent: Record<string, Coverage> | undefined,
): EcommerceAdd => {
  return {
    currencyCode: "EUR",
    add: {
      products: [mapProduct(product, channel, coverageContent)],
    },
  };
};

export const getEcommerceRemove = (
  product: Quote,
  channel: Channel,
  coverageContent: Record<string, Coverage> | undefined,
): EcommerceRemove => {
  return {
    currencyCode: "EUR",
    remove: {
      products: [mapProduct(product, channel, coverageContent)],
    },
  };
};

export const getEcommerceEntry = (
  productType: ProductType,
  channel: Channel,
): EcommerceEntry => {
  return {
    currencyCode: "EUR",
    detail: {
      products: [getEmptyProduct(productType, channel)],
    },
  };
};

export const getEcommerceCheckoutOption = (
  products: Quote[],
  channel: Channel,
  option: string,
  coverageContent: Record<string, Coverage> | undefined,
): EcommerceCheckoutOption => {
  return {
    currencyCode: "EUR",
    checkout_option: {
      actionField: {
        step: 1,
        option,
      },
      products: products
        .filter((p) => !!p.committedAt)
        .map((product) => mapProduct(product, channel, coverageContent)),
    },
  };
};

export const getEcommerceCheckout = (
  products: Quote[],
  channel: Channel,
  coverageContent: Record<string, Coverage> | undefined,
): EcommerceCheckout => {
  return {
    currencyCode: "EUR",
    checkout: {
      actionField: {
        step: 1,
      },
      products: products
        .filter((p) => !!p.committedAt)
        .map((product) => mapProduct(product, channel, coverageContent)),
    },
  };
};

export const getEcommerceVerifyAndBuy = (
  products: Quote[],
  channel: Channel,
  coverageContent: Record<string, Coverage> | undefined,
): EcommerceVerifyAndBuy => {
  return {
    currencyCode: "EUR",
    checkout: {
      actionField: {
        step: 2,
        option: "no-checkout-options",
      },
      products: products
        .filter((p) => !!p.committedAt)
        .map((product) => mapProduct(product, channel, coverageContent)),
    },
  };
};

export const getEcommercePurchase = (
  contextId: string,
  offer: OfferViewModel,
  channel: Channel,
  coverageContent: Record<string, Coverage> | undefined,
): EcommercePurchase | undefined => {
  if (!offer?.quotes || !offer.offer?.id) {
    return;
  }
  return {
    currencyCode: "EUR",
    purchase: {
      actionField: {
        affiliation: "if.fi",
        id: contextId,
        revenue: getOfferPrice(offer),
      },
      products: offer.quotes
        .filter((quote) => !!quote.committedAt)
        .map((quote) => mapProduct(quote, channel, coverageContent)),
    },
  };
};

export const getEmptyProduct = (
  productType: ProductType,
  channel: Channel,
): EcommerceProduct => {
  return {
    id: getAgpId(productType, channel),
    name: getProductName(productType),
    price: undefined,
    brand: "if.fi",
    category: undefined,
    variant: undefined,
    quantity: 1,
  };
};

const mapProduct = (
  product: Quote,
  channel: Channel,
  coverageContent: Record<string, Coverage> | undefined,
): EcommerceProduct => {
  return {
    id: getAgpId(product.productType, channel),
    name: getProductName(product?.productType),
    price: product?.annualNetPrice ?? 0,
    brand: "if.fi",
    category: undefined,
    variant: getCoverage(product, coverageContent),
    quantity: 1,
  };
};
